import type { ReactNode } from "react";

import { classes } from "./classes.js";
import {
  BAY_SLOT_CLASSES,
  BANK_BG_BAY,
  BANK_PAGE_LIST,
  BANK_PAGE_WORK,
  BANK_TEXT_BAY,
  WORK_GRID_CLASSES,
  PAGE_LARGEUR,
} from "./tokens.js";

/**
 * `D-1018-6`, round `DX-1`: **the one column every screen's content sits in.**
 *
 * The console's audit of 18 October found five different content boxes in one app (1104, 1024
 * and 768 px, a banner wider than the screen under it) because each screen set its own
 * `mx-auto max-w-*`. The width and the left edge are decided HERE, once, from `PAGE_LARGEUR`;
 * a screen is a leaf inside it and never sets a width of its own (`PAGE_MESURE` exists for a
 * form's measure INSIDE the column).
 *
 * **Danger sentence.** The column is `w-full` with `mx-auto`, so below the bound it is the
 * window and above it it is centred at the bound. Giving a screen a `min-w-*` wider than the
 * window, or a `shrink-0` child wider than the column, is what makes the page scroll
 * sideways; the shell does not clip, because a clipped page hides content instead of fixing it.
 */
export interface PageColumnProps {
  readonly children: ReactNode;
  readonly className?: string;
  /**
   * `F-0c`, R-3: **the bank's column.** Absent, the column is the one above and nothing changes for any other app.
   * `bank` is 1 280 px for a list screen; with a `bay` it is a work screen: a column of at most 1 180 px beside the
   * bay (408 px, 368 under 1 440 px; the bay stacks under the column below 1 100 px: `max-[1099px]`).
   */
  readonly variant?: "bank";
  /** The underwriter's bay, drawn by the app (this component knows what a column is, not what Hakili says). */
  readonly bay?: ReactNode;
  /** The bay's accessible name, from the catalogue. */
  readonly bayLabel?: string;
}

export function PageColumn({
  children,
  className,
  variant,
  bay,
  bayLabel,
}: PageColumnProps): React.JSX.Element {
  if (variant === "bank") {
    if (bay === undefined) {
      return (
        <div
          data-testid="page-column"
          data-variant="bank"
          className={classes("mx-auto w-full p-[var(--hk-gutter)]", BANK_PAGE_LIST, className)}
        >
          {children}
        </div>
      );
    }
    return (
      <div data-testid="page-column" data-variant="bank" className={WORK_GRID_CLASSES}>
        <div className={classes("mx-auto w-full min-w-0 p-[var(--hk-gutter)]", BANK_PAGE_WORK, className)}>
          {children}
        </div>
        <aside
          aria-label={bayLabel}
          data-kit="bay"
          className={classes(BAY_SLOT_CLASSES, BANK_BG_BAY, BANK_TEXT_BAY)}
        >
          {bay}
        </aside>
      </div>
    );
  }
  return (
    <div data-testid="page-column" className={classes("mx-auto w-full p-6", PAGE_LARGEUR, className)}>
      {children}
    </div>
  );
}
